/**
 * Reading the reporter's own text for something that identifies their record.
 *
 * A browser-side twin of bugcatch-api/src/settings/reference-extract.ts, and
 * deliberately only the half this side needs. The server does the extraction
 * that counts — what a payload claims it found is not evidence, and the value
 * an investigation eventually joins on is the one the API read out of the text
 * it stored. This copy exists to answer one question, locally and instantly:
 *
 *   does what this person has already typed contain an identifier, or do we
 *   have to interrupt them for one?
 *
 * Getting that wrong in the lenient direction costs an unnecessary question.
 * Getting it wrong in the strict direction means asking a reporter who has
 * already told us, in the sentence above the box, which is the fastest way to
 * make somebody close a bug reporter and not come back. So the matching rule
 * here is the same rule, run over the same patterns, which the API ships with
 * the field list precisely so that both sides can agree.
 *
 * The patterns arrive over the wire and are compiled here. That is a thing to
 * be deliberate about rather than nervous about: they are shapes, not secrets —
 * "a loan number looks like LN- followed by digits" is not a fact worth hiding
 * from somebody already looking at their own loan number — and the alternative
 * is a round trip per keystroke or a question for everybody.
 */

const MAX_SCAN_CHARS = 20000;
const MAX_VALUE_CHARS = 120;
const MAX_MATCHES = 200;

/**
 * Compile, or null.
 *
 * Null on anything that will not compile, which in a browser is a real case
 * rather than a defensive one: a regex feature one engine lacks throws here
 * rather than failing to match. A field that cannot be compiled is simply not
 * scanned, the reporter gets asked, and the server — which compiled it fine —
 * still reads the text. Degrading to the question is the whole design.
 */
function compile(source) {
  if (typeof source !== 'string' || !source.trim() || source.length > 400) return null;
  try {
    return new RegExp(source.trim(), 'gi');
  } catch {
    return null;
  }
}

const tidy = (value) => String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_VALUE_CHARS);

/**
 * The value as the customer's database stores it — the same rule as
 * normalizeReferenceValue in bugcatch-api/src/settings/reference-extract.ts,
 * driven by the `normalize` the API sends with each built-in field.
 */
export function normalizeValue(field, value) {
  const how = field && field.normalize;
  if (how === 'phone') {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) return digits;
    if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
    return value;
  }
  if (how === 'upper') return value.toUpperCase();
  return value;
}

/** Group 1 where the pattern has one, the whole match otherwise. */
function valueOf(match) {
  const raw = match.length > 1 && match[1] !== undefined ? match[1] : match[0];
  if (typeof raw !== 'string' || !raw.trim()) return null;
  // The span claimed is the whole match, never the capture: a labelled rule has
  // eaten the words in front of the value too, and leaving them unclaimed lets
  // a later field match them again.
  return { value: tidy(raw), start: match.index, end: match.index + match[0].length };
}

/**
 * Every configured identifier found in the given text.
 *
 * Fields are taken in the order the project arranged them and a span an earlier
 * field claimed is not offered to a later one — which is what keeps the rule for
 * "alternate number" and the rule for "mobile number", which match the same ten
 * digits, from both claiming it.
 */
export function matchReferences(fields, texts) {
  const found = [];
  const seen = new Set();

  for (const block of texts || []) {
    const text = typeof block === 'string' ? block.slice(0, MAX_SCAN_CHARS) : '';
    if (!text) continue;

    const claimed = [];
    const overlaps = (a, b) => claimed.some(([s, e]) => a < e && b > s);

    for (const field of fields || []) {
      if (!field || field.scan === false) continue;
      const re = compile(field.pattern);
      if (!re) continue;

      let match;
      let guard = 0;
      re.lastIndex = 0;
      while ((match = re.exec(text)) !== null) {
        if (++guard > MAX_MATCHES) break;
        // exec does not advance past a zero-width match, so this would spin.
        if (match[0] === '') { re.lastIndex += 1; continue; }

        const hit = valueOf(match);
        if (hit && !overlaps(hit.start, hit.end)) {
          claimed.push([hit.start, hit.end]);
          const value = normalizeValue(field, hit.value);
          const dedupe = `${field.key}:${value.toLowerCase()}`;
          if (!seen.has(dedupe)) {
            seen.add(dedupe);
            found.push({ key: field.key, label: field.label, value });
          }
        }
      }
    }
  }

  return found;
}

/** Letters the match read outside its value — the label words, roughly. */
function labelWeight(text, hit) {
  const around = text.slice(hit.start, hit.valueStart) + text.slice(hit.valueEnd, hit.end);
  return (around.match(/[a-z]/gi) || []).length;
}

/**
 * Things in the text that look like an identifier nobody labelled: a UUID, a
 * run of six or more digits, a code mixing letters and digits. Only ever used
 * to pre-fill the question, never to skip it.
 */
// No lookbehind: older Safari throws on it at parse time, which here would
// take the whole SDK down rather than one field. Boundaries are checked by hand.
const LOOSE_ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[A-Za-z0-9][\w-]{5,40}/gi;
const WORDISH = /[\w-]/;

/**
 * What the reporter's text says about which record this is, sorted by how
 * sure we can be.
 *
 *   found     — each value one field reads best, and the only value for that
 *               field. Enough to send without asking.
 *   conflicts — a value two fields read equally well (two bare UUID rules), or
 *               a field that found two different values. Asking which is the
 *               only honest answer; picking the first is a coin toss filed as
 *               a fact.
 *   loose     — ID-shaped strings no rule claimed, with the fields whose value
 *               shape they fit. Offered as the answer when we have to ask
 *               anyway, so the reporter confirms instead of retyping.
 *
 * "Reads best" is how many label letters the match consumed: `partner user id
 * 4821` is matched by the partner rule and the plain user rule, and the one
 * that read "partner" knows more. Two rules that read the same words tie, and
 * a tie is a question.
 */
export function readReferences(fields, texts) {
  const scannable = (fields || []).filter((f) => f && f.scan !== false && f.pattern);
  const hits = [];
  const loose = [];
  const blocks = (texts || []).map((t) => (typeof t === 'string' ? t.slice(0, MAX_SCAN_CHARS) : ''));

  blocks.forEach((text, block) => {
    if (!text) return;
    scannable.forEach((field, order) => {
      const re = compile(field.pattern);
      if (!re) return;
      let match;
      let guard = 0;
      re.lastIndex = 0;
      while ((match = re.exec(text)) !== null) {
        if (++guard > MAX_MATCHES) break;
        if (match[0] === '') { re.lastIndex += 1; continue; }
        const hit = valueOf(match);
        if (!hit) continue;
        const raw = match.length > 1 && match[1] !== undefined ? match[1] : match[0];
        const valueStart = match.index + Math.max(0, match[0].indexOf(raw));
        const h = {
          block, order, key: field.key, label: field.label, value: normalizeValue(field, hit.value),
          start: hit.start, end: hit.end, valueStart, valueEnd: valueStart + raw.length,
        };
        h.weight = labelWeight(text, h);
        hits.push(h);
      }
    });
  });

  // A hit loses to one that covers the same value and read more label.
  const survivors = hits.filter((h) => !hits.some((o) => o !== h && o.block === h.block
    && o.key !== h.key && o.valueStart <= h.valueStart && o.valueEnd >= h.valueEnd
    && o.weight > h.weight));

  // Group what survived by value: one value, several fields = which kind is it?
  const byValue = new Map();
  for (const h of survivors) {
    const id = h.value.toLowerCase();
    if (!byValue.has(id)) byValue.set(id, { value: h.value, keys: new Map() });
    const entry = byValue.get(id);
    if (!entry.keys.has(h.key)) entry.keys.set(h.key, { key: h.key, label: h.label, order: h.order });
  }

  const found = [];
  const conflicts = [];
  const settledKey = new Map();   // key -> values found for it
  for (const entry of byValue.values()) {
    const kinds = [...entry.keys.values()].sort((a, b) => a.order - b.order);
    if (kinds.length > 1) {
      conflicts.push({ value: entry.value, kinds: kinds.map(({ key, label }) => ({ key, label })) });
      continue;
    }
    const [{ key, label }] = kinds;
    if (!settledKey.has(key)) settledKey.set(key, []);
    settledKey.get(key).push({ key, label, value: entry.value });
  }
  for (const list of settledKey.values()) {
    if (list.length === 1) found.push(list[0]);
    else for (const r of list) conflicts.push({ value: r.value, kinds: [{ key: r.key, label: r.label }] });
  }

  // Loose candidates: ID-shaped, untouched by any rule, and fitting at least
  // one field's value shape. Nothing that is a URL path or an email.
  const seen = new Set(byValue.keys());
  blocks.forEach((text, block) => {
    if (!text) return;
    const taken = hits.filter((h) => h.block === block);
    const re = new RegExp(LOOSE_ID.source, 'gi');
    let m;
    let guard = 0;
    while ((m = re.exec(text)) !== null && ++guard <= MAX_MATCHES) {
      const s = m.index;
      const e = s + m[0].length;
      if (taken.some((h) => s < h.end && e > h.start)) continue;
      const before = text[s - 1] || '';
      const after = text[e] || '';
      if (WORDISH.test(before) || WORDISH.test(after)) continue;
      // Part of a URL, an address or a file name, not a thing someone quoted.
      if (before === '/' || before === '@' || after === '@' || before === '.') continue;
      if (after === '.' && /\w/.test(text[e + 1] || '')) continue;
      if (!/\d/.test(m[0])) continue;
      const value = tidy(m[0]);
      const id = value.toLowerCase();
      if (seen.has(id)) continue;
      const kinds = (fields || [])
        .filter((f) => f && validateReference(f, value))
        .map(({ key, label }) => ({ key, label }));
      if (!kinds.length) continue;
      seen.add(id);
      loose.push({ value, kinds });
    }
  });

  return { found, conflicts, loose };
}

/**
 * Whether what somebody typed into the box is the shape that field asks for.
 *
 * Anchored, unlike the scan above: reading prose means finding a value inside a
 * sentence, and checking an answer means the answer *is* the value. `valuePattern`
 * where the field has one — a rule that finds "alternate number: 98…" in prose
 * needs the words, and a box already headed "Alternate number" will not have
 * them typed into it again.
 *
 * Returns the tidied value, or null. Advisory only: the widget uses it to say
 * "that does not look like a loan number" before sending, and the API keeps a
 * value that fails it rather than dropping what the reporter believes is their
 * reference.
 */
export function validateReference(field, raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value || value.length > MAX_VALUE_CHARS) return null;

  const re = compile(field && (field.valuePattern || field.pattern));
  if (!re) return null;

  let match;
  let guard = 0;
  re.lastIndex = 0;
  while ((match = re.exec(value)) !== null) {
    if (++guard > MAX_MATCHES) break;
    if (match[0] === '') { re.lastIndex += 1; continue; }
    // One character of slack at the front and only there, for a pattern whose
    // left boundary consumes rather than looks behind — see the phone rule,
    // written that way so it compiles in older Safari.
    const toEnd = match.index + match[0].length === value.length;
    const atEdge = match.index === 0 || match.index === 1;
    if (toEnd && atEdge) {
      const hit = valueOf(match);
      if (hit) return normalizeValue(field, hit.value);
    }
  }
  return null;
}

/**
 * What this project needs in order to look a report up.
 *
 * Every failure answers `{ policy: 'off', fields: [] }`, which is the answer
 * that draws nothing. A widget that could not reach this route must not be a
 * widget that refuses to send a report — the whole feature is a help, and the
 * server reads the text either way.
 */
export async function referenceFields({ apiBase, apiKey, projectId }) {
  const off = { policy: 'off', fields: [] };
  try {
    const res = await fetch(
      `${apiBase}/ingest/reference-fields?projectId=${encodeURIComponent(projectId)}`,
      { headers: { 'X-BugCatch-Key': apiKey } },
    );
    if (!res.ok) return off;
    const body = await res.json();
    const policy = body.policy === 'optional' || body.policy === 'required' ? body.policy : 'off';
    const fields = Array.isArray(body.fields) ? body.fields : [];
    return fields.length ? { policy, fields } : off;
  } catch {
    return off;
  }
}
